import { useEffect, useState } from 'react';
import { X, Mail, CheckCircle2 } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { formatDateInTz, formatLocalDateInTz } from '../../../utils/timezone';
import type { NoticeKind, NoticePreview, NoticeResult } from '../../../types/nursery';

/**
 * Email a load's customers — "your order is on the 14 July run", or "your
 * trees are on their way" once it has left. Shows exactly who gets what
 * before anything is sent: a customer with no email address is named, not
 * silently skipped, and one already told about this date is not told again.
 */

export interface NoticeDialogProps {
  loadId: number;
  kind: NoticeKind;
  timezone: string;
  onClose: () => void;
  onSent: (result: NoticeResult) => void;
}

const OUTCOME: Record<NoticeResult['results'][number]['outcome'], string> = {
  sent: 'Sent',
  no_email: 'No email address',
  already_told: 'Already told',
  failed: 'Not sent',
};

export function NoticeDialog({ loadId, kind, timezone, onClose, onSent }: NoticeDialogProps) {
  const [preview, setPreview] = useState<NoticePreview | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [result, setResult] = useState<NoticeResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.previewLoadNotices(loadId, kind)
      .then((res) => (res.success ? setPreview(res.data) : setError(errorMessage(res, 'Could not prepare the emails'))))
      .catch((e) => setError(errorMessage(e, 'Could not prepare the emails')));
  }, [loadId, kind]);

  const toSend = (preview?.orders ?? []).filter((o) => o.email && !o.already_told);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.sendLoadNotices(loadId, kind);
      if (res.success) { setResult(res.data); onSent(res.data); }
      else setError(errorMessage(res, 'Could not send'));
    } catch (e) {
      setError(errorMessage(e, 'Could not send'));
    } finally {
      setBusy(false);
    }
  };

  const title = kind === 'scheduled' ? 'Tell customers when their trees go' : 'Tell customers their trees are on the way';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="notice-title">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <h2 id="notice-title" className="text-lg font-semibold text-neutral-900">{title}</h2>
            {preview && (
              <p className="text-sm text-neutral-500 break-words">
                From FactreeTech on behalf of {preview.nursery_name}.
                {preview.contact_email
                  ? ` The email asks them to contact you at ${preview.contact_email}.`
                  : ' Add a contact email on your organisation so customers know how to reach you.'}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
          {result && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-900">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{result.results.filter((r) => r.outcome === 'sent').length} sent.</span>
            </div>
          )}
          {!preview ? (
            !error && <div className="h-24 bg-neutral-100 rounded-lg animate-pulse" />
          ) : (
            <ul className="space-y-2">
              {preview.orders.map((o) => {
                const outcome = result?.results.find((r) => r.order_id === o.order_id);
                return (
                  <li key={o.order_id} className="border border-neutral-200 rounded-lg p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-medium text-neutral-900 break-words">{o.customer_name}</span>
                      <span className="text-neutral-500">{o.order_number}</span>
                      <span className="ml-auto text-xs">
                        {outcome ? (
                          <span className={outcome.outcome === 'sent' ? 'text-green-700' : outcome.outcome === 'failed' ? 'text-red-700' : 'text-neutral-500'}>
                            {OUTCOME[outcome.outcome]}{outcome.error ? `: ${outcome.error}` : ''}
                          </span>
                        ) : !o.email ? (
                          <span className="text-amber-700">No email address</span>
                        ) : o.already_told ? (
                          <span className="text-neutral-500">
                            Told{o.told_at ? ` ${formatDateInTz(o.told_at, timezone)}` : ''}
                          </span>
                        ) : (
                          <span className="text-neutral-700 break-all">{o.email}</span>
                        )}
                      </span>
                    </div>
                    {kind === 'scheduled' && o.told_for_date && !o.already_told && !outcome && (
                      <p className="text-xs text-neutral-500 mt-1">
                        Told about {formatLocalDateInTz(o.told_for_date, timezone)}; the run has moved, so they will be told the new date.
                      </p>
                    )}
                    <button type="button" className="mt-1 text-xs text-primary-700 underline"
                      onClick={() => setOpen(open === o.order_id ? null : o.order_id)}>
                      {open === o.order_id ? 'Hide the email' : 'See the email'}
                    </button>
                    {open === o.order_id && (
                      <div className="mt-2 rounded bg-neutral-50 border border-neutral-200 p-2">
                        <p className="text-xs font-semibold text-neutral-800 break-words">{o.subject}</p>
                        <pre className="mt-1 text-xs text-neutral-700 whitespace-pre-wrap break-words font-sans">{o.text}</pre>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-5 pb-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && (
            <Button size="sm" onClick={send} disabled={busy || !preview || toSend.length === 0}>
              <Mail className="h-4 w-4" /> {busy ? 'Sending…' : toSend.length ? `Send ${toSend.length} email${toSend.length === 1 ? '' : 's'}` : 'Nobody left to tell'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default NoticeDialog;
